import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
} from "react";
import { buildPlanFromChallenge } from "../Pages/Purchase/Components/challengePlans";
import { API_BASE_URL } from "../config/api";
import useInfinitePaginated from "../hooks/useInfinitePaginated";

const CHALLENGE_API_URL =
  `${API_BASE_URL}landing/challenges/`;

const ChallengeContext = createContext(null);

const ChallengeProvider = ({ children }) => {
  const { data: challengeData, initialLoading: isLoading, loadingMore, error, hasNext, sentinelRef, retry, reload: fetchChallengeData } =
    useInfinitePaginated({ endpoint: CHALLENGE_API_URL, authenticated: false });

  const plans = useMemo(() => {
    if (!Array.isArray(challengeData)) {
      return [];
    }
    return challengeData
      .map(buildPlanFromChallenge)
      .filter((plan) => plan?.planName);
  }, [challengeData]);

  const challengePlans = useMemo(() => {
    return plans.reduce((acc, plan) => {
      if (plan?.planName) {
        acc[plan.planName] = plan;
      }
      return acc;
    }, {});
  }, [plans]);

  const defaultPlanName = plans[0]?.planName ?? "";

  const getPlanByName = useCallback(
    (planName) => {
      return challengePlans[planName] ?? challengePlans[defaultPlanName];
    },
    [challengePlans, defaultPlanName],
  );

  const value = useMemo(
    () => ({
      challengeData,
      challengePlans,
      plans,
      defaultPlanName,
      getPlanByName,
      isLoading,
      error,
      hasNext,
      loadingMore,
      sentinelRef,
      retry,
      refetch: fetchChallengeData,
    }),
    [
      challengeData,
      challengePlans,
      plans,
      defaultPlanName,
      getPlanByName,
      isLoading,
      error,
      hasNext,
      loadingMore,
      sentinelRef,
      retry,
      fetchChallengeData,
    ],
  );

  return (
    <ChallengeContext.Provider value={value}>
      {children}
    </ChallengeContext.Provider>
  );
};

const useChallenge = () => {
  const context = useContext(ChallengeContext);
  if (!context) {
    throw new Error("useChallenge must be used within ChallengeProvider");
  }
  return context;
};

export { ChallengeProvider, useChallenge, buildPlanFromChallenge };
